import { computed } from "vue";
import { useRouter } from "vue-router";

import { useIdentityQueueStore } from "@/stores/identityQueueStore";

import useDynamicPricingCalculator from "@/composables/helpers/useDynamicPricingCalculator";
import { useGlobalStore } from "@/stores/globalStore";

export default () => {
  const router = useRouter();
  const { dynamicPricingCalculator } = useDynamicPricingCalculator();
  const globalStore = useGlobalStore();
  const defaultData = {
    urlData: {},
    orderMode: null,
    disableUIKeys: {},
    areDefaultsFromUrl: false,
  };

  const calculateByCoinOrFiat = async (merchApiData, form, store) => {
    const { lockFiat, fiatAmount, coinAmount } = merchApiData;
    let type;
    let value;

    // lockFiat is the pivot
    if (lockFiat) {
      type = "fiatAmount";
      value = fiatAmount;
    } else {
      type = "coinAmount";
      value = coinAmount;
    }
    await dynamicPricingCalculator(type, value, form, store);
  };

  const checkAndMutateIfFiatAndCoinTypesAreValid = (
    key,
    value,
    fiatInputs,
    coinInputs
  ) => {
    if (key === "fiatType" && !fiatInputs.includes(value)) {
      const [newDefaultFiatValue] = fiatInputs;
      return newDefaultFiatValue;
    }

    if (key === "coinType" && !coinInputs.includes(value)) {
      const [newDefaultCoinValue] = coinInputs;
      return newDefaultCoinValue;
    }

    return "";
  };

  /**
   * Method binds business logic to push to the Vue form ref Object
   * and update the store using updateValue action.
   * @param {Object} data
   * @param {Ref Object} form
   * @param {Object} store
   * @returns {}
   * */
  const setFormRefsAndUpdateStore = async (
    merchApiData,
    form,
    store,
    fiatInputs,
    coinInputs
  ) => {
    if (merchApiData.fiatAmount === null && merchApiData.coinAmount === null) {
      merchApiData.fiatAmount = 100;
    }

    const orderData = Object.entries(merchApiData);
    const keysToRelatedToOrder = Object.keys(store.form);
    const keysRelatedToOtp = Object.keys(store.otpData);

    const storeHasNoErrors = computed(() => !store.formHasErrors);
    const { redirectTo } = merchApiData;
    const globalStore = useGlobalStore();

    orderData.forEach(([key, value]) => {
      if (keysRelatedToOtp.includes(key) && value) {
        store.otpData[key] = value;
        store.updateValue(key, value);
      }

      if (!keysToRelatedToOrder.includes(key) || !value) {
        if (key === "account_reference") {
          globalStore.setCustomerReference(value);
        }
        return;
      }

      if (key === "fiatType" || key === "coinType") {
        const newUnSupportedDefaultValue =
          checkAndMutateIfFiatAndCoinTypesAreValid(
            key,
            value,
            fiatInputs,
            coinInputs
          );

        if (newUnSupportedDefaultValue) {
          store.updateValue(key, newUnSupportedDefaultValue);
          form.value[key] = newUnSupportedDefaultValue;
          store.form[key].fromApi = true;
          return;
        }
      }

      store.form[key].fromApi = true;
      store.form[key].value = value;
      form.value[key] = value;
    });

    await calculateByCoinOrFiat(merchApiData, form, store);

    let canSkipError = false;
    if (storeHasNoErrors.value) {
      canSkipError = true;
    }
    const errorInForm = store.getErrorInForm;
    if (
      store.form.walletAddress?.value !== "" &&
      errorInForm.length === 1 &&
      errorInForm[0] === "walletAddress"
    ) {
      // if wallet address is not empty, it should have been validated from BE. Therefore, it's fine to skip error in FE validation.
      canSkipError = true;
    }

    if (
      !redirectTo ||
      redirectTo === "order" ||
      !canSkipError ||
      globalStore.getMerchantSettings?.referredWalletAddressIsEditable
    ) {
      return;
    }

    if (redirectTo.indexOf("verification") >= 0) {
      router.push("/" + redirectTo);
      return;
    }
    const identityQueueStore = useIdentityQueueStore();
    await identityQueueStore.fetchIdentityDocuments();
    router.push(identityQueueStore.getNextIdentityRoute);
  };

  /**
   * Sifts through the api order data to find relevant keys' values
   * filled and if so, then to provide a Boolean for that field to be
   * disabled, and set within the reduced object to be returned.
   *
   * @param {Array} orderData (Array passed through Object.entries)
   * @param {Function} callback
   * @returns {Object}
   * */

  const disableUiByApi = (merchApiData) => {
    const keysRelatedToApiToDisableUiBy = [
      "coinType",
      "walletAddress",
      "walletAddressTag",
      "blockchain",
    ];

    if (globalStore?.global?.merchant?.readOnlyAmounts) {
      keysRelatedToApiToDisableUiBy.push("coinAmount");
      keysRelatedToApiToDisableUiBy.push("fiatAmount");
      keysRelatedToApiToDisableUiBy.push("fiatType");
    }
    const orderData = Object.entries(merchApiData);

    return orderData.reduce((acc, [key, value]) => {
      if (
        !keysRelatedToApiToDisableUiBy.includes(key) ||
        (!["coinAmount", "fiatAmount"].includes(key) && !value)
      ) {
        return acc;
      }

      if (
        globalStore.getMerchantSettings?.referredWalletAddressIsEditable &&
        key.includes("walletAddress")
      ) {
        return acc;
      }

      acc[key] = true;
      return acc;
    }, {});
  };

  return {
    setFormRefsAndUpdateStore,
    disableUiByApi,
    defaultData,
  };
};
